declare global {
  interface Window {
    Trustpilot?: {
      loadFromElement: (element: Element, force?: boolean) => void;
    };
  }
}

const SCRIPT_SRC =
  'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';

const READY_TIMEOUT_MS = 3000;
const READY_POLL_MS = 50;

type ScriptWithComplete = HTMLScriptElement & { complete?: boolean };

let loadPromise: Promise<void> | null = null;

function isScriptComplete(script: HTMLScriptElement): boolean {
  return Boolean((script as ScriptWithComplete).complete);
}

function waitForTrustpilot(timeoutMs = READY_TIMEOUT_MS): Promise<void> {
  if (window.Trustpilot) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const startedAt = Date.now();

    const tick = () => {
      if (window.Trustpilot) {
        resolve();
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error('Trustpilot bootstrap timed out'));
        return;
      }

      window.setTimeout(tick, READY_POLL_MS);
    };

    tick();
  });
}

/**
 * 确保 Trustpilot bootstrap 只加载一次，组件内自动调用，页面无需再写 script
 */
export function ensureTrustpilotBootstrap(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.resolve();
  }

  if (window.Trustpilot) {
    return Promise.resolve();
  }

  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    const settle = (result: Promise<void>) => {
      result.then(resolve, error => {
        loadPromise = null;
        reject(error);
      });
    };

    const existing = document.querySelector<HTMLScriptElement>(
      `script[src*="tp.widget.bootstrap.min.js"]`,
    );

    if (existing) {
      settle(
        new Promise<void>((res, rej) => {
          if (window.Trustpilot) {
            res();
            return;
          }

          const onLoad = () => {
            waitForTrustpilot().then(res, rej);
          };
          const onError = () => {
            rej(new Error('Trustpilot bootstrap failed to load'));
          };

          existing.addEventListener('load', onLoad, { once: true });
          existing.addEventListener('error', onError, { once: true });

          // 已完成或在绑监听前后刚好完成时，load 不会再触发
          if (isScriptComplete(existing) || window.Trustpilot) {
            existing.removeEventListener('load', onLoad);
            existing.removeEventListener('error', onError);
            waitForTrustpilot().then(res, rej);
          }
        }),
      );
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => {
      settle(waitForTrustpilot());
    };
    script.onerror = () => {
      loadPromise = null;
      reject(new Error('Trustpilot bootstrap failed to load'));
    };
    document.head.appendChild(script);
  });

  return loadPromise;
}
